import React from 'react';

import PropTypes from 'prop-types';

import { getBrandValue } from '~/shared/utils/brandNameHelper';
import slugify from '~/techstyle-shared/react-components/src/utils/slugify';

// For use with @techstyle/react-components ProductListingFilter `colorSwatches` prop
export const getColorSwatches = () => {
  return getBrandValue({
    savage: savageColorSwatches,
    default: fableticsColorSwatches,
  });
};

export const savageColorSwatches = {
  Black: '#000000',
  Blue: '#2950C0',
  Brown: '#BA9983',
  Green: '#46D060',
  Grey: '#B7B6B6',
  Nude: '#BA9983',
  Orange: '#FF9567',
  Pink: '#FDC8E5',
  Purple: '#B9ACE8',
  Red: '#FD3E3E',
  White: '#FFFFFF',
  Yellow: '#FFF156',
};

export const fableticsColorSwatches = {
  'Berry Blast': '#7C3766',
  'Blue Lagoon': '#418987',
  'Caribbean Blue': '#205366',
  'Cashmere Pink': '#ee7da6',
  'Ceil Blue': '#779dd7',
  'Classic White': '#EDF4FF',
  'Cosmic Pink': '#B6687D',
  'Desert Shadows': '#776C6B',
  'Electric Teal': '#008799',
  'Elemental Blue': '#5E7892',
  'Galaxy Blue': '#212193',
  'Goji Red': '#be222f',
  'Green Mystique': '#254242',
  'Heather Grey': '#939393',
  'Heather Pink': '#F7BCD3',
  'Holly Berry': '#7F1C25',
  'Hunter Green': '#395743',
  'Moonlight Mauve': '#7E535C',
  'Pink Marshmallow': '#E2AECB',
  'Rose Quartz': '#AD969B',
  'Royal Blue': '#194B82',
  'Surgical Green': '#00AE8A',
  'Utility Green': '#5B5645',
  'Vintage Grey': '#6F7984',
  Acai: '#45334E',
  Black: '#000',
  Blue: '#1a77c6',
  Bonsai: '#46473F',
  Brown: '#c15d07',
  Capri: '#006B6A',
  Chai: '#B5A08B',
  Cloudberry: '#89637F',
  Coral: '#FA9A98',
  Gelato: '#C7536A',
  Green: '#87ad56',
  Grey: '#858583',
  Hibiscus: '#A42662',
  Lilac: '#B88AAC',
  Navy: '#252C51',
  Onyx: '#4B4C4E',
  Orange: '#ff8c4a',
  Oxblood: '#361C1D',
  Pink: '#eab7b7',
  Purple: '#c869f4',
  Red: '#ff1133',
  Sage: '#768981',
  Seaglass: '#81B1AB',
  Seaside: '#6DCACE',
  Ultraviolet: '#543a99',
  Wave: '#6193A3',
  White: '#fff',
  Wine: '#751c28',
  Wisteria: '#676693',
  Yellow: '#ffff00',
};

/**
 * Checks if the given facet name and option value should use image swatches.
 * This enables CDN-based filter display for textures/patterns.
 * Supports both full facet matching and specific value matching.
 *
 * Config format:
 * {
 *   "Denim Wash": true,              // All values use images
 *   "Color": ["Multi", "Print"],     // Only these values use images
 *   "Pattern": true                  // All values use images
 * }
 *
 * @param {string} facetName - The name of the facet to check.
 * @param {string} optionValue - The specific option value to check.
 * @param {Object} imageSwatchFilters - Config object mapping facets to true (all values) or array of values.
 * @return {boolean} - Returns true if the facet/option should use image swatches.
 */
export const isImageSwatchFacet = ({
  facetName,
  optionValue = null,
  imageSwatchFilters = null,
}) => {
  if (!imageSwatchFilters || !facetName) {
    return false;
  }

  // Check if facet exists in config
  const facetConfig = imageSwatchFilters[facetName];

  if (!facetConfig) {
    return false;
  }

  // If facetConfig is true, match all values
  if (facetConfig === true) {
    return true;
  }

  // If facetConfig is an array, check if optionValue is in the array
  if (Array.isArray(facetConfig) && optionValue !== null) {
    return facetConfig.includes(optionValue);
  }

  return false;
};

/**
 * Generates a CDN URL for a filter image swatch.
 * Example: ("Denim Wash", "Light Wash", "https://cdn.fabletics.com/media/images")
 *          -> "https://cdn.fabletics.com/media/images/filters/denim_wash/light_wash.jpg"
 *
 * @param {string} filterName - The filter name (e.g., "Denim Wash")
 * @param {string} optionValue - The option value (e.g., "Light Wash")
 * @param {string} cdnBaseUrl - The CDN base URL (from config)
 * @return {string} - The generated CDN URL
 */
export const generateFilterImageUrl = (filterName, optionValue, cdnBaseUrl) => {
  const sanitizedFilter = slugify(filterName, '_');
  const sanitizedOption = slugify(optionValue, '_');
  return `${cdnBaseUrl}/filters/${sanitizedFilter}/${sanitizedOption}.jpg`;
};

/**
 * Factory function that creates a getSwatchIcon function for use with ProductListingFilter.
 * Returns a function that generates appropriate swatch icons based on image swatch config.
 *
 * @param {Object} params - Configuration parameters
 * @param {string} params.facetName - The facet/field name
 * @param {Object} params.colorSwatches - Color swatch configuration object
 * @param {Object} params.imageSwatchFilters - Image swatch filter configuration
 * @param {string} params.filterImageCdnUrl - CDN base URL for filter images
 * @param {Object} params.circleIconStyle - Styled component style for circle icons
 * @param {React.Component} params.CircleIconComponent - The CircleIcon component to render
 * @return {Function} - Function that takes {value} and returns a CircleIcon component
 */
export const createGetSwatchIcon = ({
  facetName,
  colorSwatches,
  imageSwatchFilters,
  filterImageCdnUrl,
  circleIconStyle,
  CircleIconComponent,
}) => {
  const SwatchIcon = ({ value }) => {
    // Check if this filter should use image swatches
    const shouldUseImageSwatch = isImageSwatchFacet({
      facetName,
      optionValue: value,
      imageSwatchFilters,
    });

    if (shouldUseImageSwatch) {
      const imageUrl = generateFilterImageUrl(
        facetName,
        value,
        filterImageCdnUrl
      );
      return (
        <CircleIconComponent
          value={value}
          colorSwatches={colorSwatches}
          circleIconStyle={circleIconStyle}
          backgroundImage={imageUrl}
        />
      );
    }

    // Default to color swatch
    return (
      <CircleIconComponent
        value={value}
        colorSwatches={colorSwatches}
        showBorder={value === 'White'}
        circleIconStyle={circleIconStyle}
      />
    );
  };

  SwatchIcon.propTypes = {
    value: PropTypes.string.isRequired,
  };

  SwatchIcon.displayName = 'SwatchIcon';

  return SwatchIcon;
};

/**
 * Determines the appropriate filter style based on whether the facet uses image swatches.
 *
 * @param {string} facetName - The facet/field name
 * @param {Object} imageSwatchFilters - Image swatch filter configuration
 * @param {string} swatchStyle - The style constant to return for swatch filters
 * @return {string|null} - Returns the swatch style if facet uses image swatches, null otherwise
 */
export const getFilterStyle = (facetName, imageSwatchFilters, swatchStyle) => {
  return isImageSwatchFacet({
    facetName,
    imageSwatchFilters,
  })
    ? swatchStyle
    : null;
};
